/**
 * Agent Frontend Types
 */

// Agent types supported by the platform
export type AgentType =
  | 'chat'
  | 'research'
  | 'task'
  | 'data-analysis'
  | 'code-assistant'
  | 'support'
  | 'web-browsing'
  | 'multi-agent'
  | 'custom';

// Builder types for agent creation
export type BuilderType = 'form' | 'canvas' | 'prompt' | 'json' | 'template';

// Canvas data for visual workflow builder
export interface CanvasData {
  nodes: Array<{
    id: string;
    type: string;
    position: { x: number; y: number };
    data: Record<string, unknown>;
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    sourceHandle?: string;
    targetHandle?: string;
  }>;
  viewport?: { x: number; y: number; zoom: number };
}

// Template categories
export type TemplateCategory =
  | 'customer-service'
  | 'customer-support'
  | 'development'
  | 'research'
  | 'automation'
  | 'data-analysis'
  | 'data'
  | 'creative'
  | 'productivity'
  | 'multi-agent'
  | 'other';

// LLM providers
export type LLMProvider = 'anthropic' | 'openai' | 'google';

// Execution status
export type ExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

// Agent configuration
export interface AgentConfig {
  llmProvider: LLMProvider;
  model?: string;
  systemPrompt?: string;
  tools?: string[];
  maxIterations?: number;
  temperature?: number;
  persona?: {
    name?: string;
    description?: string;
    tone?: string;
  };
  memory?: {
    type?: 'buffer' | 'summary' | 'vector';
    maxMessages?: number;
  };
}

// Agent definition
export interface Agent {
  id: string;
  name: string;
  description: string | null;
  agent_type: AgentType;
  config: AgentConfig;
  is_public: boolean;
  is_active: boolean;
  organization_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Extended fields
  builder_type: BuilderType;
  canvas_data: CanvasData | null;
  template_id: string | null;
  version: number;
  is_draft: boolean;
}

// Tool call result
export interface ToolCallResult {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  result?: unknown;
  error?: string;
  durationMs: number;
}

// Agent execution
export interface AgentExecution {
  id: string;
  agent_id: string;
  session_id: string;
  user_id: string;
  organization_id: string;
  status: ExecutionStatus;
  input: { message: string; context?: Record<string, unknown> };
  output: { content: string; toolCalls?: ToolCallResult[] } | null;
  usage: { inputTokens: number; outputTokens: number; totalTokens: number } | null;
  duration_ms: number | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

// Agent output from execution
export interface AgentOutput {
  content: string;
  toolCalls?: ToolCallResult[];
  usage: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  iterations: number;
}

// SSE Event types
export type AgentEventType =
  | 'thinking'
  | 'token'
  | 'tool_start'
  | 'tool_end'
  | 'step_start'
  | 'step_end'
  | 'iteration'
  | 'done'
  | 'error'
  | 'heartbeat';

// Agent streaming event
export interface AgentEvent {
  type: AgentEventType;
  content?: string;
  toolName?: string;
  toolArgs?: Record<string, unknown>;
  toolResult?: unknown;
  toolError?: string;
  iteration?: number;
  output?: AgentOutput;
  error?: string;
  timestamp: string;
}

// Conversation message
export interface ConversationMessage {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  timestamp: string;
  toolName?: string;
  toolCallId?: string;
}

// Session memory
export interface SessionMemory {
  messages: ConversationMessage[];
}

// Agent session
export interface AgentSession {
  id: string;
  session_id: string;
  agent_id: string;
  user_id: string;
  memory: SessionMemory;
  summary: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Session list item
export interface SessionListItem {
  id: string;
  session_id: string;
  agent_id: string;
  message_count: number;
  summary: string | null;
  created_at: string;
  updated_at: string;
}

// Input types
export interface CreateAgentInput {
  name: string;
  description?: string;
  agentType: AgentType;
  config: AgentConfig;
  isPublic?: boolean;
  // Extended fields
  builderType?: BuilderType;
  canvasData?: CanvasData;
  templateId?: string;
  isDraft?: boolean;
}

export interface UpdateAgentInput {
  name?: string;
  description?: string;
  agentType?: AgentType;
  config?: Partial<AgentConfig>;
  isPublic?: boolean;
  isActive?: boolean;
  // Extended fields
  builderType?: BuilderType;
  canvasData?: CanvasData;
  isDraft?: boolean;
}

export interface AgentInput {
  message: string;
  sessionId?: string;
  context?: Record<string, unknown>;
}

// List options
export interface AgentListOptions {
  type?: AgentType;
  active?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ExecutionListOptions {
  status?: ExecutionStatus;
  limit?: number;
  offset?: number;
}

// Response types
export interface AgentResponse {
  success: boolean;
  data: Agent;
}

export interface AgentListResponse {
  success: boolean;
  data: Agent[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ExecutionResponse {
  success: boolean;
  data: AgentOutput;
}

export interface ExecutionDetailResponse {
  success: boolean;
  data: AgentExecution;
}

export interface ExecutionListResponse {
  success: boolean;
  data: AgentExecution[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface SessionListResponse {
  success: boolean;
  data: SessionListItem[];
}

export interface SessionDetailResponse {
  success: boolean;
  data: AgentSession;
}

export interface MessageResponse {
  success: boolean;
  message: string;
}

// Agent type metadata for UI
export const AGENT_TYPE_INFO: Record<
  AgentType,
  { label: string; description: string; icon: string }
> = {
  chat: {
    label: 'Chat Agent',
    description: 'Conversational agent with memory and persona support',
    icon: 'MessageSquare',
  },
  research: {
    label: 'Research Agent',
    description: 'RAG-powered agent for document retrieval and research',
    icon: 'Search',
  },
  task: {
    label: 'Task Agent',
    description: 'Multi-step workflow automation agent',
    icon: 'ListTodo',
  },
  'data-analysis': {
    label: 'Data Analysis Agent',
    description: 'Database queries and data visualization',
    icon: 'BarChart',
  },
  'code-assistant': {
    label: 'Code Assistant',
    description: 'Code generation, review, and debugging',
    icon: 'Code',
  },
  support: {
    label: 'Support Agent',
    description: 'Customer support with intent routing',
    icon: 'Headphones',
  },
  'web-browsing': {
    label: 'Web Browsing Agent',
    description: 'Web research and content extraction',
    icon: 'Globe',
  },
  'multi-agent': {
    label: 'Multi-Agent',
    description: 'Orchestrate multiple agents to work together',
    icon: 'Users',
  },
  custom: {
    label: 'Custom Agent',
    description: 'Fully customizable agent configuration',
    icon: 'Settings',
  },
};

// LLM provider metadata for UI
export const LLM_PROVIDER_INFO: Record<
  LLMProvider,
  { label: string; models: string[] }
> = {
  anthropic: {
    label: 'Anthropic Claude',
    models: ['claude-sonnet-4-20250514', 'claude-3-5-sonnet-20241022', 'claude-3-haiku-20240307'],
  },
  openai: {
    label: 'OpenAI GPT',
    models: ['gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  },
  google: {
    label: 'Google Gemini',
    models: ['gemini-2.0-flash', 'gemini-1.5-pro', 'gemini-1.5-flash'],
  },
};

// Template definition
export interface AgentTemplate {
  id: string;
  name: string;
  description: string | null;
  category: TemplateCategory;
  agent_type: AgentType;
  config: AgentConfig;
  canvas_data: Record<string, unknown> | null;
  preview_image_url: string | null;
  tags: string[];
  is_official: boolean;
  usage_count: number;
  created_at: string;
}

// Template responses
export interface TemplateResponse {
  success: boolean;
  data: AgentTemplate;
}

export interface TemplateListResponse {
  success: boolean;
  data: AgentTemplate[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// Template category metadata
export const TEMPLATE_CATEGORY_INFO: Record<
  TemplateCategory,
  { label: string; description: string; icon: string }
> = {
  'customer-service': {
    label: 'Customer Service',
    description: 'Help desk and customer service agents',
    icon: 'Headphones',
  },
  'customer-support': {
    label: 'Customer Support',
    description: 'Help desk and customer service agents',
    icon: 'Headphones',
  },
  development: {
    label: 'Development',
    description: 'Code review, debugging, and development assistance',
    icon: 'Code',
  },
  research: {
    label: 'Research',
    description: 'Information gathering and analysis',
    icon: 'Search',
  },
  automation: {
    label: 'Automation',
    description: 'Task automation and workflow optimization',
    icon: 'Settings',
  },
  'data-analysis': {
    label: 'Data Analysis',
    description: 'Data analysis and visualization',
    icon: 'BarChart',
  },
  data: {
    label: 'Data & Analytics',
    description: 'Data analysis and visualization',
    icon: 'BarChart',
  },
  creative: {
    label: 'Creative',
    description: 'Content creation and creative tasks',
    icon: 'Sparkles',
  },
  productivity: {
    label: 'Productivity',
    description: 'Task automation and workflow optimization',
    icon: 'ListTodo',
  },
  'multi-agent': {
    label: 'Multi-Agent',
    description: 'Teams of specialized agents working together',
    icon: 'Users',
  },
  other: {
    label: 'Other',
    description: 'Miscellaneous templates',
    icon: 'Settings',
  },
};

// ============================================
// Crew Types
// ============================================

// Crew process types
export type CrewProcess = 'sequential' | 'hierarchical' | 'parallel' | 'consensual';

// Agent role in a crew
export interface AgentRole {
  id: string;
  name: string;
  description: string;
  goal: string;
  backstory?: string;
  capabilities: string[];
  tools?: string[];
  llmConfig?: {
    provider?: LLMProvider;
    model?: string;
    temperature?: number;
  };
  allowDelegation?: boolean;
  maxIterations?: number;
}

// Task in a crew
export interface CrewTask {
  id: string;
  description: string;
  expectedOutput: string;
  assignedAgent: string;
  dependencies?: string[];
  context?: string[];
  priority?: number;
  async?: boolean;
}

// Crew configuration
export interface CrewConfig {
  llmProvider?: LLMProvider;
  model?: string;
  verbose?: boolean;
  maxExecutionTime?: number;
  sharedMemory?: boolean;
  enableCommunication?: boolean;
  maxCommunicationRounds?: number;
}

// Crew definition
export interface Crew {
  id: string;
  name: string;
  description: string | null;
  process_type: CrewProcess;
  manager_agent_id: string | null;
  members: AgentRole[];
  tasks: CrewTask[];
  config: CrewConfig;
  organization_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Crew execution status
export type CrewExecutionStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

// Crew execution record
export interface CrewExecution {
  id: string;
  crew_id: string;
  user_id: string;
  organization_id: string;
  status: CrewExecutionStatus;
  input: Record<string, unknown>;
  output: Record<string, unknown> | null;
  task_outputs: Record<string, unknown>;
  interactions: Record<string, unknown>[];
  token_usage: { prompt: number; completion: number; total: number } | null;
  duration_ms: number | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

// Crew responses
export interface CrewResponse {
  success: boolean;
  data: Crew;
}

export interface CrewListResponse {
  success: boolean;
  data: Crew[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface CrewExecutionResponse {
  success: boolean;
  data: CrewExecution;
}

// Crew input types
export interface CreateCrewInput {
  name: string;
  description?: string;
  processType: CrewProcess;
  managerAgentId?: string;
  members: AgentRole[];
  tasks: CrewTask[];
  config?: CrewConfig;
}

export interface UpdateCrewInput {
  name?: string;
  description?: string;
  processType?: CrewProcess;
  managerAgentId?: string | null;
  members?: AgentRole[];
  tasks?: CrewTask[];
  config?: Partial<CrewConfig>;
}

// Crew process metadata
export const CREW_PROCESS_INFO: Record<
  CrewProcess,
  { label: string; description: string; icon: string }
> = {
  sequential: {
    label: 'Sequential',
    description: 'Tasks execute one after another in order',
    icon: 'ArrowRight',
  },
  hierarchical: {
    label: 'Hierarchical',
    description: 'Manager agent coordinates and delegates tasks',
    icon: 'GitBranch',
  },
  parallel: {
    label: 'Parallel',
    description: 'Independent tasks execute simultaneously',
    icon: 'GitMerge',
  },
  consensual: {
    label: 'Consensual',
    description: 'Agents discuss and agree on outputs',
    icon: 'MessageCircle',
  },
};
